// Drawing Guess — the live drawing: a per-table stroke log, the painter that
// renders it onto any 2D canvas (the easel's CanvasTexture, the VR pad, the
// screen overlay), the drawer's pen, and the sync over gameBroadcast.
//
// Strokes are visuals only (the server never sees them, nothing is decided
// from them). Coordinates are normalised to the canvas (0–1 → ints 0–1000,
// x right, y down) so every surface shows the same picture at its own size.
//
// Wire format (event 'draw' on gameBroadcast, ≤ ~15 msgs/s while drawing):
//   { k, op: 'pts', id, c, s, p: [x,y,x,y…] }  new points of stroke `id` (created on first sight)
//   { k, op: 'undo', id }                       remove a stroke
//   { k, op: 'clear' }                          wipe the canvas
//   { k, op: 'req', d? }                        "send me the drawing" (late joiner; d = the drawer is asking)
//   { k, op: 'snap', strokes, part, of }        answer to req, chunked
// `k` = `${gameId}:${turnNo}` — messages for another turn are ignored.
//
// Late joiners: the drawer keeps the whole (simplified) stroke log and answers
// 'req' with a snapshot; if the drawer is the one asking (they reloaded), the
// lowest-seated guesser with a drawing answers instead.

import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { useGameFx, sendGameFx, fxBatcher } from '../gameBroadcast';
import type { TableView } from '../gameStore';
import type { DrawState } from '../../../../supabase/functions/games/drawing.ts';

export const COLORS = ['#16181d', '#ffffff', '#e53935', '#fb8c00', '#fdd835', '#43a047', '#1e88e5', '#8e24aa'];
export const COLOR_NAMES = ['Black', 'White', 'Red', 'Orange', 'Yellow', 'Green', 'Blue', 'Purple'];
/** Brush diameters as a fraction of the canvas width. */
export const SIZES = [0.005, 0.012, 0.028];
export const SIZE_NAMES = ['Thin', 'Medium', 'Thick'];
/** Canvas aspect (width / height) — the easel, the pad and the overlay all use it. */
export const ASPECT = 3 / 2;
export const Q = 1000;
const EVENT = 'draw';
const MIN_STEP = 4;          // drop points closer than this (in 0–1000 units) to the last one
const SNAP_CHUNK = 6000;     // numbers per snapshot message (~30 KB)

export interface Stroke { id: string; c: number; s: number; p: number[] }

interface Board {
  id: string;
  key: string;
  strokes: Stroke[];
  /** Bumps on every change. */
  version: number;
  /** Bumps when strokes were removed / replaced (painters redraw from scratch). */
  epoch: number;
}

const boards = new Map<string, Board>();

export function getBoard(tableId: string): Board {
  let b = boards.get(tableId);
  if (!b) boards.set(tableId, (b = { id: tableId, key: '', strokes: [], version: 0, epoch: 0 }));
  return b;
}

/** Stroke count per table, for React bits that care (Undo / Clear enabled) — changes per stroke, not per point. */
const useStrokeCounts = create<{ counts: Record<string, number> }>(() => ({ counts: {} }));
export const useStrokeCount = (tableId: string) => useStrokeCounts((s) => s.counts[tableId] ?? 0);

function changed(b: Board, wipe: boolean) {
  b.version++;
  if (wipe) b.epoch++;
  const cur = useStrokeCounts.getState().counts;
  if (cur[b.id] !== b.strokes.length) useStrokeCounts.setState({ counts: { ...cur, [b.id]: b.strokes.length } });
}

/** Switch the board to turn `key` (clears it when the turn changed). */
export function setBoardKey(tableId: string, key: string) {
  const b = getBoard(tableId);
  if (b.key === key) return;
  b.key = key;
  b.strokes = [];
  changed(b, true);
}

export const turnKey = (table: TableView | null | undefined): string => {
  const g = table?.state.g as DrawState | null | undefined;
  return table && g ? `${table.state.gameId ?? ''}:${g.turnNo}` : '';
};

const clampQ = (v: number) => Math.max(0, Math.min(Q, Math.round(v * Q)));

// ---- painting ---------------------------------------------------------------

/**
 * Paints a board onto a canvas incrementally: new points of the newest
 * strokes are drawn as they arrive; undo / clear / snapshots redraw it all.
 * `paint()` returns true when the canvas changed (→ texture.needsUpdate).
 */
export class BoardPainter {
  private epoch = -1;
  private version = -1;
  private drawn = new Map<string, number>(); // stroke id → numbers already painted
  constructor(public canvas: HTMLCanvasElement, private background = '#ffffff') {}

  paint(b: Board, force = false): boolean {
    if (!force && b.version === this.version && b.epoch === this.epoch) return false;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return false;
    const W = this.canvas.width, H = this.canvas.height;
    if (force || b.epoch !== this.epoch) {
      ctx.fillStyle = this.background;
      ctx.fillRect(0, 0, W, H);
      this.drawn.clear();
      this.epoch = b.epoch;
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const st of b.strokes) {
      const done = this.drawn.get(st.id) ?? 0;
      if (done >= st.p.length) continue;
      ctx.strokeStyle = ctx.fillStyle = COLORS[st.c] ?? COLORS[0];
      const lw = Math.max(1, (SIZES[st.s] ?? SIZES[1]) * W);
      ctx.lineWidth = lw;
      const x = (i: number) => (st.p[i] / Q) * W, y = (i: number) => (st.p[i + 1] / Q) * H;
      if (done === 0) {
        // A dot, so a tap shows up (and joins smoothly with what follows).
        ctx.beginPath(); ctx.arc(x(0), y(0), lw / 2, 0, Math.PI * 2); ctx.fill();
      }
      ctx.beginPath();
      const from = Math.max(0, done - 2);
      ctx.moveTo(x(from), y(from));
      for (let i = from + 2; i < st.p.length; i += 2) ctx.lineTo(x(i), y(i));
      ctx.stroke();
      this.drawn.set(st.id, st.p.length);
    }
    this.version = b.version;
    return true;
  }
}

/** Ramer–Douglas–Peucker on a flat [x,y,…] list (tolerance in 0–1000 units). */
export function simplify(p: number[], tol = 2.5): number[] {
  const n = p.length / 2;
  if (n <= 2) return p.slice();
  const keep = new Uint8Array(n); keep[0] = keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    const ax = p[a * 2], ay = p[a * 2 + 1], bx = p[b * 2], by = p[b * 2 + 1];
    const dx = bx - ax, dy = by - ay, len = Math.hypot(dx, dy) || 1;
    let best = -1, bi = -1;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs(dy * p[i * 2] - dx * p[i * 2 + 1] + bx * ay - by * ax) / len;
      if (d > best) { best = d; bi = i; }
    }
    if (best > tol && bi > 0) { keep[bi] = 1; stack.push([a, bi], [bi, b]); }
  }
  const out: number[] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(p[i * 2], p[i * 2 + 1]);
  return out;
}

// ---- the drawer's pen ---------------------------------------------------------

/** Pen colour / size, shared by every input surface on this device. */
export const usePenStyle = create<{ color: number; size: number; setColor: (c: number) => void; setSize: (s: number) => void }>((set) => ({
  color: 0,
  size: 1,
  setColor: (color) => set({ color }),
  setSize: (size) => set({ size }),
}));

let strokeSeq = 0;
const myTag = Math.random().toString(36).slice(2, 7);

export interface Pen {
  /** Start a stroke at normalised (0–1) x, y. */
  down(x: number, y: number): void;
  move(x: number, y: number): void;
  up(): void;
  undo(): void;
  clear(): void;
  readonly drawing: boolean;
}

/**
 * The drawer's pen for table `tableId`: draws locally at once and streams the
 * points (batched ~15/s) to everyone else. Only call it while you're drawing.
 */
export function createPen(worldId: string, tableId: string): Pen {
  let cur: Stroke | null = null;
  const send = (d: Record<string, unknown>) => sendGameFx(worldId, tableId, EVENT, { k: getBoard(tableId).key, ...d });
  const batch = fxBatcher<number>((nums) => { if (nums.length) send({ op: 'pts', id: lastId, c: lastC, s: lastS, p: nums }); }, 66, 120);
  let lastId = '', lastC = 0, lastS = 1;
  const add = (x: number, y: number, force = false) => {
    if (!cur) return;
    const qx = clampQ(x), qy = clampQ(y);
    const n = cur.p.length;
    if (!force && n >= 2 && Math.hypot(qx - cur.p[n - 2], qy - cur.p[n - 1]) < MIN_STEP) return;
    cur.p.push(qx, qy);
    batch.push(qx); batch.push(qy);
    changed(getBoard(tableId), false);
  };
  return {
    get drawing() { return !!cur; },
    down(x, y) {
      if (cur) this.up();
      const { color, size } = usePenStyle.getState();
      cur = { id: `${myTag}-${++strokeSeq}`, c: color, s: size, p: [] };
      lastId = cur.id; lastC = color; lastS = size;
      getBoard(tableId).strokes.push(cur);
      add(x, y, true);
    },
    move(x, y) { add(x, y); },
    up() {
      if (!cur) return;
      batch.flush();
      // Keep the log compact for snapshots (what's on screen is unchanged enough).
      const b = getBoard(tableId);
      cur.p = simplify(cur.p);
      cur = null;
      changed(b, false);
    },
    undo() {
      if (cur) this.up();
      const b = getBoard(tableId);
      const last = b.strokes.pop();
      if (!last) return;
      changed(b, true);
      send({ op: 'undo', id: last.id });
    },
    clear() {
      if (cur) this.up();
      const b = getBoard(tableId);
      if (!b.strokes.length) return;
      b.strokes = [];
      changed(b, true);
      send({ op: 'clear' });
    },
  };
}

/** One pen per table on this device (overlay, VR pad and easel share it). */
const pens = new Map<string, Pen>();
export function penFor(worldId: string, tableId: string): Pen {
  let p = pens.get(tableId);
  if (!p) pens.set(tableId, (p = createPen(worldId, tableId)));
  return p;
}

// ---- sync ---------------------------------------------------------------------

const isNum = (v: unknown) => typeof v === 'number' && Number.isFinite(v);
const cleanPts = (p: unknown): number[] =>
  Array.isArray(p) ? p.slice(0, 4000).filter(isNum).map((v) => Math.max(0, Math.min(Q, Math.round(v as number)))) : [];
const cleanStroke = (s: any): Stroke | null =>
  s && typeof s.id === 'string' && s.id.length < 40
    ? { id: s.id, c: isNum(s.c) ? Math.max(0, Math.min(COLORS.length - 1, s.c | 0)) : 0, s: isNum(s.s) ? Math.max(0, Math.min(SIZES.length - 1, s.s | 0)) : 1, p: cleanPts(s.p) }
    : null;

/**
 * Keeps this device's board for `table` in step with the drawer: listens for
 * stroke messages, asks for the drawing when it arrives mid-turn, and — as the
 * drawer — answers those requests. Mount once per table (the easel does).
 */
export function useDrawingSync(worldId: string, table: TableView | null, seat: number) {
  const tableId = table?.id ?? null;
  const g = table?.state.g as DrawState | null | undefined;
  const key = turnKey(table);
  const live = !!g && table?.state.phase === 'playing' && g.phase === 'drawing';
  const amDrawer = live && g!.drawer === seat;
  // Answer requests as the drawer, or (when the drawer asks) as the lowest-seated guesser.
  const seatedGuessers = table ? table.state.seats.map((s, i) => (s && g && i !== g.drawer ? i : -1)).filter((i) => i >= 0) : [];
  const backupResponder = seat >= 0 && seatedGuessers[0] === seat;
  const role = useMemo(() => ({ amDrawer, backupResponder }), [amDrawer, backupResponder]);

  // A new turn (or the end of drawing) clears the board.
  useEffect(() => { if (tableId && key) setBoardKey(tableId, key); }, [tableId, key]);

  // Snapshot parts being collected (by sender).
  const pending = useMemo(() => new Map<string, Stroke[]>(), []);
  const answer = useMemo(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    return {
      schedule(fn: () => void) { if (!t) t = setTimeout(() => { t = null; fn(); }, 250); },
      cancel() { if (t) clearTimeout(t); t = null; },
    };
  }, []);
  useEffect(() => () => answer.cancel(), [answer]);

  useGameFx(worldId, tableId, EVENT, (d, from) => {
    if (!tableId || !d || typeof d !== 'object') return;
    const b = getBoard(tableId);
    if (d.k !== b.key || !b.key) return;
    switch (d.op) {
      case 'pts': {
        if (role.amDrawer) return; // the drawer's own board is the truth
        const pts = cleanPts(d.p);
        let st = b.strokes.find((s) => s.id === d.id);
        if (!st) {
          const made = cleanStroke({ id: d.id, c: d.c, s: d.s, p: [] });
          if (!made) return;
          b.strokes.push((st = made));
        }
        st.p.push(...pts);
        changed(b, false);
        return;
      }
      case 'undo': {
        if (role.amDrawer) return;
        const i = b.strokes.findIndex((s) => s.id === d.id);
        if (i >= 0) { b.strokes.splice(i, 1); changed(b, true); }
        return;
      }
      case 'clear':
        if (role.amDrawer) return;
        b.strokes = []; changed(b, true);
        return;
      case 'req': {
        const shouldAnswer = d.d ? role.backupResponder : role.amDrawer;
        if (!shouldAnswer || (d.d && !b.strokes.length)) return;
        answer.schedule(() => {
          const board = getBoard(tableId);
          if (board.key !== d.k) return;
          // Chunk by size so no single message is large.
          const parts: Stroke[][] = [[]];
          let size = 0;
          for (const s of board.strokes) {
            if (size + s.p.length > SNAP_CHUNK && parts[parts.length - 1].length) { parts.push([]); size = 0; }
            parts[parts.length - 1].push({ id: s.id, c: s.c, s: s.s, p: s.p.slice() });
            size += s.p.length;
          }
          parts.forEach((strokes, part) => setTimeout(() => {
            sendGameFx(worldId, tableId, EVENT, { k: board.key, op: 'snap', strokes, part, of: parts.length });
          }, part * 120));
        });
        return;
      }
      case 'snap': {
        if (role.amDrawer && b.strokes.length) return;
        const src = String(from ?? 'x');
        const list = pending.get(src) ?? [];
        for (const s of Array.isArray(d.strokes) ? d.strokes.slice(0, 2000) : []) { const c = cleanStroke(s); if (c) list.push(c); }
        pending.set(src, list);
        if (Number(d.part) + 1 < Number(d.of)) return;
        pending.delete(src);
        // Snapshot first, then anything that streamed in live meanwhile.
        const ids = new Set(list.map((s) => s.id));
        b.strokes = [...list, ...b.strokes.filter((s) => !ids.has(s.id))];
        changed(b, true);
        return;
      }
    }
  });

  // Arriving mid-drawing (or after a reload): ask for what's on the easel.
  useEffect(() => {
    if (!tableId || !live || !key) return;
    const ask = () => {
      const b = getBoard(tableId);
      if (b.key === key && !b.strokes.length) sendGameFx(worldId, tableId, EVENT, { k: key, op: 'req', d: amDrawer ? 1 : 0 });
    };
    const t1 = setTimeout(ask, 900);
    const t2 = setTimeout(ask, 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // Only when the turn changes or we start following it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, key, live, worldId]);
}
